import { memo, useState, useCallback, useContext } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import { IconPencil, IconHeadset } from "@tabler/icons-react";
import Swal from "sweetalert2";
import UserContext from "../../../contexts/UserContext";
import { patchUser } from "../../Users/services/UserServicesV2";
import "./OperatorNameBadge.css";

const OperatorNameBadge = memo(() => {
  const { user, setUser } = useContext(UserContext);
  const [showModal, setShowModal] = useState(false);
  const [operatorName, setOperatorName] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const fullName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
  const displayName = user?.operator_name || fullName || "Operador";

  const openModal = useCallback(() => {
    setOperatorName(user?.operator_name || "");
    setShowModal(true);
  }, [user?.operator_name]);

  const handleSave = useCallback(async () => {
    const userId = user?.user_id || user?.id;
    if (!userId) return;

    setIsSaving(true);
    try {
      const value = operatorName.trim() || null;
      await patchUser(userId, { operator_name: value });

      // Atualizar o contexto do usuário
      setUser((prev) => ({ ...prev, operator_name: value || "" }));

      setShowModal(false);
      Swal.fire({
        icon: "success",
        title: "Nome atualizado",
        text: value
          ? `Suas mensagens serão enviadas como "${value}"`
          : `Suas mensagens serão enviadas como "${fullName}"`,
        timer: 2000,
        showConfirmButton: false,
      });
    } catch {
      Swal.fire({
        icon: "error",
        title: "Erro",
        text: "Não foi possível atualizar o nome do operador",
      });
    } finally {
      setIsSaving(false);
    }
  }, [user?.id, operatorName, fullName, setUser]);

  return (
    <>
      <button
        className="sc-operator-badge"
        onClick={openModal}
        title="Editar nome de operador"
      >
        <IconHeadset size={14} className="sc-operator-badge__icon" />
        <span className="sc-operator-badge__name">{displayName}</span>
        <IconPencil size={12} className="sc-operator-badge__edit" />
      </button>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        centered
        size="sm"
      >
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>Nome do Operador</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p
            className="text-muted mb-3"
            style={{ fontSize: 13, lineHeight: 1.5 }}
          >
            Este nome será exibido nas mensagens enviadas pela Central de
            Atendimento. Se vazio, será usado seu nome completo.
          </p>

          <Form.Group>
            <Form.Label style={{ fontSize: 13, fontWeight: 500 }}>
              Nome para atendimento
            </Form.Label>
            <Form.Control
              type="text"
              value={operatorName}
              onChange={(e) => setOperatorName(e.target.value)}
              placeholder={fullName || "Seu nome"}
              maxLength={70}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSave();
              }}
            />
            <Form.Text className="text-muted">
              {operatorName.trim()
                ? `Mensagens serão enviadas como: *${operatorName.trim()}*`
                : `Mensagens serão enviadas como: *${fullName}*`}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setShowModal(false)}
          >
            Cancelar
          </Button>
          <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner animation="border" size="sm" /> : "Salvar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
});

OperatorNameBadge.displayName = "OperatorNameBadge";

export default OperatorNameBadge;
